import {
  Box,
  Button,
  Checkbox,
  Field,
  Flex,
  Input,
  OptionType,
  Select,
  toast,
} from '@workwhile/ui'
import { useCreatePositionRequirementMutation } from 'queries/company/useCreatePositionRequirementMutation'
import React, { useState } from 'react'
import type { SingleValue } from 'react-select'
import { RequirementCategory } from 'types/requirementCategories'

const CATEGORY_OPTIONS = Object.values(RequirementCategory)

const CATEGORY_SELECT_OPTIONS: OptionType[] = CATEGORY_OPTIONS.map((c) => ({
  label: c,
  value: c,
}))

interface Props {
  positionId: number | null
  successCallback: () => void
}

const CreateRequirementForPosition = ({
  positionId,
  successCallback,
}: Props) => {
  const [displayName, setDisplayName] = useState('')
  const [category, setCategory] = useState(CATEGORY_OPTIONS[0])
  const [description, setDescription] = useState('')
  const [isWorkerVisible, setIsWorkerVisible] = useState(true)
  const [isCompanySpecific, setIsCompanySpecific] = useState(false)

  const {
    mutate: createPositionRequirement,
    isPending: isCreatePositionRequirementPending,
  } = useCreatePositionRequirementMutation()

  const validateAndCreateRequirement = () => {
    if (!displayName) {
      toast.error('Please enter a display name')
      return
    }
    if (!category) {
      toast.error('Please select a category')
      return
    }
    if (!positionId) {
      toast.error('Position ID is required')
      return
    }

    const requirementData = {
      name: displayName,
      description: description,
      category: category,
      is_worker_visible: isWorkerVisible,
      is_company_specific: isCompanySpecific,
      position_id: positionId,
    }

    createPositionRequirement(requirementData, {
      onSuccess: (id) => {
        toast.success(
          'Requirement #' +
            id +
            ' created and added to position ' +
            positionId +
            ' ✅'
        )
        successCallback()
      },
      onError: (error) => {
        toast.error('❌ Error creating requirement')
        console.error(error)
      },
    })

    setDisplayName('')
    setDescription('')
    setIsWorkerVisible(true)
    setIsCompanySpecific(false)
    setCategory(CATEGORY_OPTIONS[0] ?? '')
  }

  function toastMessageForEngHelp(label: string) {
    toast.info(
      `Cannot make a requirement ${label} until talking to eng team. Continue with non-${label} and then reach out to eng with the ID of the created requirement.`
    )
  }

  const handleCategoryChange = (opt: SingleValue<OptionType>) => {
    if (opt !== null && typeof opt.value === 'string') {
      setCategory(opt.value as RequirementCategory)
    }
  }

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      maxWidth="100%"
      width="100%"
    >
      <Flex flexDirection="column" width="100%" gap={3}>
        <Field label="Display name">
          <Input
            block
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display Name"
          />
        </Field>
        <Field label="Description (optional)">
          <Input
            block
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
          />
        </Field>
        <Box>
          <Checkbox
            value={isWorkerVisible}
            onChange={(checked) => setIsWorkerVisible(checked)}
          >
            Visible to worker?
          </Checkbox>
        </Box>
        <Box>
          <Checkbox
            value={false}
            onChange={() => toastMessageForEngHelp('verifiable')}
          >
            Verifiable?
          </Checkbox>
        </Box>
        <Field label="Category">
          <Select<OptionType>
            block
            options={CATEGORY_SELECT_OPTIONS}
            value={
              CATEGORY_SELECT_OPTIONS.find((o) => o.value === category) ?? null
            }
            onChange={handleCategoryChange}
          />
        </Field>
        <Box>
          <Checkbox
            value={isCompanySpecific}
            onChange={(checked) => setIsCompanySpecific(checked)}
          >
            Company specific?
          </Checkbox>
        </Box>
        <Box>
          <Checkbox
            value={false}
            onChange={() => toastMessageForEngHelp('must have')}
          >
            Must have?
          </Checkbox>
        </Box>
      </Flex>
      <Button
        type="button"
        mt={3}
        onClick={() => validateAndCreateRequirement()}
        disabled={isCreatePositionRequirementPending}
      >
        Submit
      </Button>
    </Flex>
  )
}
export default CreateRequirementForPosition
